import { useCallback, useEffect, useRef, useState } from "react";
import clsx from "clsx";

// MUI
import { Box, Menu } from "@mui/material";
// Components
import SearchInput from "@/Layouts/components/Basics/SearchInput";
import { LocationMenuItem } from "@/Layouts/views/Header/components/LocationMenuItem";
// Types
import { ILocation } from "@/Common/interfaces/location";
// Hooks
import { useAuthorization } from "@/Common/hooks/Authorization/Authorization";

const MAX_LOCATIONS = 6;
const SCROLL_TIMEOUT = 200;

interface LocationSelectorDropdownProps {
	anchorEl: Element | null;
	open: boolean;
	handleClose: () => void;
	locations: ILocation[];
	handleMenuClick: (selectedId: ILocation["locationId"]) => void;
	location: ILocation | null;
}

export const LocationSelectorDropdown = (props: Readonly<LocationSelectorDropdownProps>) => {
	const { anchorEl, open, handleClose, locations, handleMenuClick, location } = props;
	const { isPresentAccess } = useAuthorization();

	const [searchValue, setSearchValue] = useState("");
	const selectedLocationRef = useRef<HTMLLIElement>(null);

	useEffect(() => {
		setSearchValue("");
	}, [location]);

	useEffect(() => {
		const timeout = setTimeout(() => {
			if (!searchValue && selectedLocationRef.current) {
				selectedLocationRef.current.scrollIntoView({
					behavior: "smooth",
					block: "end",
					inline: "nearest",
				});
			}
		}, SCROLL_TIMEOUT);
		return () => clearTimeout(timeout);
	}, [open, searchValue]);

	const shouldShowLocation = useCallback(
		(text: string) => {
			if (searchValue === "") {
				return true;
			}
			return text.toLowerCase().includes(searchValue.toLowerCase());
		},
		[searchValue]
	);

	const handleSearchChange = (value: string) => setSearchValue(value);

	const handleSearchClear = () => setSearchValue("");

	const searchEnabled = locations.length > MAX_LOCATIONS;

	return (
		<Menu
			id="select-box-menu"
			anchorEl={anchorEl}
			open={open}
			onClose={handleClose}
			MenuListProps={{
				"aria-labelledby": "select-box-button",
				className: "!pt-2 !pb-3",
			}}
			anchorOrigin={{
				vertical: "bottom",
				horizontal: "left",
			}}
			transformOrigin={{
				vertical: "top",
				horizontal: "left",
			}}
			slotProps={{
				paper: {
					className: clsx(
						`!p-0 !mt-0 !rounded-t-none !rounded-b-[15px] !rounded-b-[10px] !-translate-x-[5px] !overflow-visible before:content-[''] before:block
					before:absolute before:top-0 before:rounded-tl-[3px] before:left-[15px] before:w-[14px] before:h-[14px] before:bg-white before:-translate-y-1/2 before:rotate-45
					before:skew-x-6 before:skew-y-6 before:z-0`,
						{
							"!pr-[6px]": searchEnabled,
						}
					),
				},
			}}
		>
			{searchEnabled && (
				<Box className="px-4 pt-1 pb-2">
					<SearchInput value={searchValue} loading={!location} onChange={handleSearchChange} clearSearch={handleSearchClear} />
				</Box>
			)}
			<Box className="max-h-48 overflow-y-auto">
				{locations?.map((item) => {
					if (!shouldShowLocation(item.name)) {
						return null;
					}
					const selected = location?._id === item._id;

					return (
						<LocationMenuItem
							ref={selected ? selectedLocationRef : undefined}
							key={item._id}
							location={item}
							handleMenuClick={handleMenuClick}
							disabled={!isPresentAccess(`ACCESS_LOCATION_${item.locationId}`)}
							selected={selected}
						></LocationMenuItem>
					);
				})}
			</Box>
		</Menu>
	);
};
